<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="xl"
            :no-close-on-backdrop="item_modified"
            :no-close-on-esc="item_modified"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @ok="save"
        >
            <template #modal-title>
                Edit
                <span v-if="edit_type && edit_type === 'walkthrough'">Slide</span>
                <span v-else-if="editable_media_item.merlin_id">Merlin</span>
                <span v-else-if="editable_media_item.related_media_id">SBP</span> Media Item
            </template>

            <div v-if="edit_type && edit_type === 'walkthrough'" class="row">
                <div class="col-lg-6 order-lg-1 order-2">
                    <b-form-group
                        label="Title"
                        label-for="title"
                    >
                        <b-form-input
                            type="text"
                            v-model="editable_media_item.title"
                            :state="validating && !editable_media_item.title ? false : null"
                        />

                        <b-form-invalid-feedback :state="validating && !editable_media_item.title ? false : null">
                            Title is required
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        v-if="!editable_media_item.related_media || (editable_media_item.related_media && editable_media_item.related_media.type !== 'sims')"
                        label="Description"
                        label-for="description"
                    >
                        <b-form-textarea v-model="editable_media_item.description" rows="2" max-rows="6"/>
                    </b-form-group>

                    <b-form-group
                        v-if="
                            (editable_media_item.related_media && editable_media_item.related_media.type === 'image')
                            || !editable_media_item.related_media
                        "
                        label="Alt"
                        label-for="alt"
                    >
                        <b-form-textarea v-model="editable_media_item.alt" rows="2" max-rows="6"/>
                    </b-form-group>

                    <b-form-group
                        v-if="!editable_media_item.related_media || (editable_media_item.related_media && editable_media_item.related_media.type !== 'sims')"
                        label="Caption"
                        label-for="caption"
                    >
                        <b-form-textarea v-model="editable_media_item.caption" rows="2" max-rows="6"/>
                    </b-form-group>

                    <b-form-group
                        v-if="!editable_media_item.related_media || (editable_media_item.related_media && editable_media_item.related_media.type !== 'sims')"
                        label="Credit"
                        label-for="credit"
                    >
                        <b-form-textarea v-model="editable_media_item.credit" rows="2" max-rows="6"/>
                    </b-form-group>

                    <div v-if="editable_media_item.related_media_id !== 0" class="alert alert-primary" role="alert">
                        <p>
                            Editing this slide will only effect this instance of the media data.
                        </p>

                        To alter the parent media data please use this
                        <strong>ID: {{ editable_media_item.related_media_id }}</strong>
                        to lookup the media in the <a href="/media" target="_blank">Media Manager</a>.
                    </div>
                </div>

                <div class="col-lg-6 order-lg-2 order-1 mb-lg-0 mb-3">
                    <template v-if="editable_media_item.related_media">
                        <div class="d-flex" v-if="editable_media_item.related_media.type === 'audio'">
                            <b-dropdown
                                v-if="edit_type && edit_type === 'walkthrough'"
                                variant="outline-primary"
                                size="sm"
                                class="ml-auto"
                            >
                                <template #button-content>
                                    Set Thumbnail
                                </template>
                                <b-dropdown-item @click="showAddRelatedMediaModal = true">
                                    <span class="text-primary">
                                        <font-awesome-icon :icon="['fad', 'photo-video']" fixed-width/>
                                    </span>
                                    Media Manager
                                </b-dropdown-item>
                                <b-dropdown-item
                                    @click="
                                        editable_media_item.splash_url = editable_media_item.related_media.splash_url
                                    "
                                >
                                    <span class="text-primary">
                                        <font-awesome-icon :icon="['fad', 'undo']" fixed-width/>
                                    </span>
                                    Parent Media Thumbnail
                                </b-dropdown-item>
                                <b-dropdown-item @click="editable_media_item.splash_url = DEFAULT_AUDIO_THUMBNAIL">
                                    <span class="text-primary">
                                        <font-awesome-icon :icon="['fad', 'volume-up']" fixed-width/>
                                    </span>
                                    Default Audio Thumbnail
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                        <div class="h-75 w-100 row align-items-center justify-content-center">
                            <div v-if="editable_media_item.related_media.type === 'image'">
                                <b-img :src="thumbnail" thumbnail/>
                            </div>
                            <div v-else-if="editable_media_item.related_media.type === 'video'">
                                <video width="100%" height="auto" controls>
                                    <source :src="editable_media_item.related_media.url" type="video/mp4">
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                            <template v-else-if="editable_media_item.related_media.type === 'audio'">
                                <div class="d-flex justify-content-center mb-2">
                                    <b-img
                                        v-if="editable_media_item.splash_url"
                                        :src="editable_media_item.splash_url"
                                        class="w-75"
                                        thumbnail
                                    />
                                </div>
                                <audio class="w-100" controls>
                                    <source :src="editable_media_item.related_media.url">
                                    Your browser does not support the audio tag.
                                </audio>
                            </template>

                            <div v-else-if="editable_media_item.related_media.type === 'sims'" class="mb-3">
                                <iframe :src="editable_media_item.related_media.url" />
                                <div class="text-center">
                                    <b-btn
                                        variant="outline-primary"
                                        :href="editable_media_item.related_media.url"
                                        target="_blank"
                                    >
                                        <font-awesome-icon
                                            :icon="['fad', 'external-link']"
                                            fixed-width
                                        />
                                        Open in new tab
                                    </b-btn>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="h-75 row px-2">
                            <b-btn
                                v-if="edit_type && edit_type === 'walkthrough'"
                                @click="showUpdateAssetModal = true"
                                class="ml-auto mb-2"
                                variant="outline-warning"
                            >
                                Replace Asset
                            </b-btn>
                            <img
                                :src="`${editable_media_item.url}?sbp_cache_buster=${cacheBusterValue}`"
                                class="h-100 w-100 pdf-thumbnail-preview"
                            />
                        </div>
                    </template>
                </div>
            </div>
            <div v-else class="row align-items-center">
                <div class="col-lg-6 order-lg-1 order-2">
                    <b-form-group
                        label="Title "
                        label-for="title"
                    >
                        <b-form-input
                            type="text"
                            v-model="editable_media_item.name"
                            :state="validating && !editable_media_item.name ? false : null"
                        />

                        <b-form-invalid-feedback :state="validating && !editable_media_item.name ? false : null">
                            Title is required
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        v-if="editable_media_item.type !== 'sims'"
                        label="Description"
                        label-for="description"
                    >
                        <b-form-textarea v-model="editable_media_item.description" rows="2" max-rows="6"/>
                    </b-form-group>

                    <b-form-group
                        v-if="editable_media_item.type === 'image' || editable_media_item.type === 'link'"
                        label="Alt"
                        label-for="alt"
                    >
                        <b-form-textarea v-model="editable_media_item.alt" rows="2" max-rows="6"/>
                    </b-form-group>

                    <b-form-group
                        v-if="editable_media_item.type !== 'sims'"
                        label="Caption"
                        label-for="caption"
                    >
                        <b-form-textarea v-model="editable_media_item.caption" rows="2" max-rows="6"/>
                    </b-form-group>

                    <b-form-group
                        v-if="editable_media_item.type !== 'sims'"
                        label="Credit"
                        label-for="credit"
                    >
                        <b-form-textarea v-model="editable_media_item.credit" rows="2" max-rows="6"/>
                    </b-form-group>

                    <div
                        v-if="editable_media_item.related_media_id"
                        class="alert alert-primary"
                        role="alert"
                    >
                        Editing the Media Item will only effect this instance of the Media.
                        To alter Media data please use this ID: {{ editable_media_item.related_media_id }}
                        to lookup the Media in the <a href="/media" target="_blank">Media Manager</a>.
                    </div>
                    <div v-else-if="editable_media_item.id" class="alert alert-danger" role="alert">
                        <strong>Editing the Media Item from Related Media will modify the original media in the Media
                            Manager.</strong> Please be sure to only make edits as necessary as to not effect other
                        related media in other articles. Use Media ID: {{ editable_media_item.id }} to lookup the Media
                        in the <a href="/media" target="_blank">Media Manager</a>.
                    </div>
                    <div v-else class="alert alert-primary" role="alert">
                        Editing the Merlin Media Item will only effect this instance of the Media.
                    </div>
                </div>

                <div class="col-lg-6 order-lg-2 order-1 mb-lg-0 mb-3">
                    <div class="text-center">
                        <div
                            v-if="editable_media_item.type === 'image' || editable_media_item.type === 'link'"
                            class="mb-lg-1"
                        >
                            <b-img :src="thumbnail" thumbnail />
                        </div>
                        <div v-else-if="editable_media_item.type === 'video'" class="mb-3">
                            <video width="100%" height="auto" controls>
                                <source
                                    :src="editable_media_item.url ? editable_media_item.url : editable_media_item.src"
                                    type="video/mp4"
                                >
                                Your browser does not support the video tag.
                            </video>
                        </div>
                        <div v-else-if="editable_media_item.type === 'audio'" class="mb-3">
                            <audio controls>
                                <source :src="editable_media_item.url">
                                Your browser does not support the audio tag.
                            </audio>
                        </div>
                        <div v-else-if="editable_media_item.type === 'sims'" class="mb-3">
                            <iframe
                                :src="editable_media_item.value ? editable_media_item.value : editable_media_item.url ? editable_media_item.url : editable_media_item.src"
                            />
                            <div class="text-center">
                                <b-btn
                                    variant="outline-primary"
                                    :href="editable_media_item.value ? editable_media_item.value : editable_media_item.url ? editable_media_item.url : editable_media_item.src"
                                    target="_blank"
                                >
                                    <font-awesome-icon
                                        :icon="['fad', 'external-link']"
                                        fixed-width
                                    />
                                    Open in new tab
                                </b-btn>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <template #modal-footer>
                <b-button
                    variant="light"
                    @click="cancel"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    @click="save"
                >
                    <font-awesome-icon :icon="['fad', 'check-square']" fixed-width/>
                    Save Changes
                </b-button>
            </template>

        </b-modal>

        <upload-pdf-modal
            :slide-id="editable_media_item.id"
            :existing-url="editable_media_item.url"
            :visible="showUpdateAssetModal"
            :article="article"
            @hide="showUpdateAssetModal = false"
            @replaced-asset="() => this.cacheBusterValue = (new Date()).getTime()"
        />
        <add-related-media-modal
            type="image"
            v-model="showAddRelatedMediaModal"
            @link="setNewThumbnailFromMedia"
            @hide="showAddRelatedMediaModal = false"
        />
    </div>
</template>

<style>
.pdf-thumbnail-preview {
    object-fit: contain;
}
</style>

<script>
import {mapState} from 'vuex'
import languages from '@/common/languages'
import LanguageMultiselect from '@/components/LanguageMultiselect'
import UploadPdfModal from '@/components/modals/article/related_media/UploadPdfModal'
import AddRelatedMediaModal from '@/components/modals/article/related_media/AddRelatedMediaModal'

export default {
    data: () => ({
        related_topics: [],
        languages: languages,
        editable_media_item: {},
        editable_media_item_cache: {},
        showUpdateAssetModal: false,
        showAddRelatedMediaModal: false,
        cacheBusterValue: (new Date()).getTime(),
    }),
    props: [
        'value',
        'item',
        'edit_type',
        'article',
    ],
    components: {
        AddRelatedMediaModal,
        LanguageMultiselect,
        UploadPdfModal,
    },
    computed: {
        ...mapState({
            validating: state => state.utilities.validating
        }),
        modal_id() {
            let modal_id = 'media-modal'
            return modal_id
        },
        item_modified() {
            let item_modified = false

            if (!_.isEqual(this.editable_media_item, this.editable_media_item_cache)) {
                item_modified = true
            }

            return item_modified
        },
        language: {
            get() {
                return {
                    language: this.editable_media_item.language
                }
            },
            set(language) {
                this.$set(this.editable_media_item, 'language', language.language)
            }
        },
        thumbnail() {
            let thumbnail = null
            if (
                this.item &&
                this.item.dam_id
            ) {
                thumbnail = this.item.url
            } else if (!this.item.splash_url) {
                thumbnail = this.item.url;
            } else {
                thumbnail = this.item.splash_url
            }
            if (this.item.image) {
                thumbnail = this.item.image
            }
            if (this.item.src) {
                thumbnail = this.item.src
            }
            if (this.edit_type === 'walkthrough' && this.item && this.item.related_media) {
                thumbnail = this.item.related_media.url
            }

            return thumbnail
        }
    },
    watch: {
        value: {
            handler(value) {
                if (value == true) {
                    this.$store.commit('setValidating', false)

                    if (this.item) {
                        this.editable_media_item = _.cloneDeep(this.item)
                        this.editable_media_item_cache = _.cloneDeep(this.item)
                    }
                }
            }
        }
    },
    methods: {
        setNewThumbnailFromMedia(media) {
            this.editable_media_item.splash_url = media.url
            this.showAddRelatedMediaModal = false
        },
        save() {
            if (this.item_modified) {
                this.$emit('update', this.editable_media_item)
            }
            this.hide()
        },
        validated() {
            let validated = true

            if (!this.editable_media_item.name) {
                validated = false
            }

            return validated
        },
        addLanguage(new_language) {
            this.language = new_language
        },
        removeLanguage() {
            this.language = {}
        },
        cancel(event) {
            if (this.item_modified) {
                event.preventDefault()

                this.$swal({
                    html: 'Changes have been made to this related media item. If you proceed, all changes will be lost.',
                    showCancelButton: true,
                    confirmButtonText: 'Discard Changes',
                    cancelButtonText: 'Keep Editing',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.hide()
                    }
                })
            } else {
                this.hide()
            }
        },
        hide() {
            this.$emit('hide')
        },
        hidden() {
            this.editable_media_item = {}
            this.editable_media_item_cache = {}

            this.$emit('hide')
        },
    },
    created() {
        this.DEFAULT_AUDIO_THUMBNAIL = '//cdn.studiesweekly.com/online/resources/pod_media/audio_default.png'
    },
}
</script>
